import { useMemo, useCallback } from 'react';
import { SortEndHandler } from 'react-sortable-hoc';
import { SubjectOption, SurveyCakeLogicEngine } from '@surveycake/logic-engine';
import {
  useSubjectAnswers,
  useSubjectOptionStates,
  useEngineInstance,
  useSubjectId,
  UseSubjectAnswersSelector
} from '@surveycake/react-logic-engine';
import { passThrough } from '@surveycake/utils';
import { useLatestValueRef } from '@surveycake/rhooks';
import { addOptionPrefix } from 'app/modules/subject';

const answersSelector: UseSubjectAnswersSelector<string[]> = answers =>
  answers.payload as string[];

function useItemSortAnswerConfig(engine: SurveyCakeLogicEngine, id: number) {
  const value = useSubjectAnswers(id, answersSelector);
  const valueRef = useLatestValueRef(value);
  const { options: originOptions, count } = useMemo(() => {
    const data = engine.getSubjectData(id);
    return {
      options: data.options!,
      count: data.lowerbounds!
    };
  }, [engine, id]);
  const optionStates = useSubjectOptionStates(id, passThrough);
  const options = useMemo(() => {
    if (!optionStates) {
      return originOptions;
    }

    return originOptions.filter(option => optionStates[option.ID].visible);
  }, [originOptions, optionStates]);
  const result = useMemo(() => {
    const unselected: SubjectOption[] = [];
    const selected: SubjectOption[] = [];

    options.forEach(option => {
      if (value.includes(addOptionPrefix(option.ID))) {
        selected.push(option);
      } else {
        unselected.push(option);
      }
    });
    selected.sort(
      (a, b) =>
        value.indexOf(addOptionPrefix(a.ID)) -
        value.indexOf(addOptionPrefix(b.ID))
    );

    return { unselected, selected };
  }, [options, value]);

  return {
    valueRef,
    count,
    unselected: result.unselected,
    selected: result.selected
  };
}

function useItemSortAnswerMethods(
  engine: SurveyCakeLogicEngine,
  id: number,
  valueRef: ReturnType<typeof useItemSortAnswerConfig>['valueRef']
) {
  const setAnswer = useCallback(
    (payload: string[]) => engine.setAnswer(id, { payload }),
    [engine, id]
  );
  const onSortEnd = useCallback<SortEndHandler>(
    ({ oldIndex: from, newIndex: to }) => {
      const payload = [...valueRef.current];
      payload.splice(
        to < 0 ? payload.length + to : to,
        0,
        payload.splice(from, 1)[0]
      );
      setAnswer(payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setAnswer]
  );
  const onSelect = useCallback((optionId: number) => {
    const payload = [...valueRef.current, addOptionPrefix(optionId)];
    setAnswer(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onRemove = useCallback(
    (optionId: number) => {
      const removeValue = addOptionPrefix(optionId);
      const payload = valueRef.current.filter(value => value !== removeValue);
      setAnswer(payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setAnswer]
  );
  const onClear = useCallback(() => setAnswer([]), [setAnswer]);

  return { onSortEnd, onSelect, onRemove, onClear };
}

export function useItemSortAnswer() {
  const engine = useEngineInstance();
  const id = useSubjectId();
  const { valueRef, count, unselected, selected } = useItemSortAnswerConfig(
    engine,
    id
  );
  const { onSortEnd, onSelect, onRemove, onClear } = useItemSortAnswerMethods(
    engine,
    id,
    valueRef
  );

  return {
    count,
    unselected,
    selected,
    onSortEnd,
    onSelect,
    onRemove,
    onClear
  };
}
